import { API_ERRORS, QUERY_STATUS } from "constants/constants";

// Codes to ignore when a more specific error already exists on the same input
const SUPERSEDED_CODES = ["user_not_found"];

// Mapping: API error code → form input(s)
// Used when the API `key` does not match the actual input name
const API_CODE_TO_INPUT = {
  // Mission: key "dates" => actual input
  worker_is_busy: "worker_id",
  invalid_worker: "worker_id",
  cslt_has_no_contract: "worker_id",
  worker_is_out: "worker_id",
  invalid_consultant: "worker_id",
  manager_has_no_contract: "manager_id",
  invalid_manager: "manager_id",
  scheduled_end_date_before_start_date: "end_date",
  end_date_before_start_date: ["end_date", "replacement_start_date"],

  // Mission Backup: key "backup_dates" / "dates" => actual input
  original_start_ge_original_end: "replacement_start_date",
  final_start_gt_backup_end: ["replacement_end_date", "departure_date_of_replacement"],

  // Mission Fee: key "dates" => actual input
  establishment_date_after_establishment_date_end: "establishment_date",
  establishment_date_too_late: "establishment_date",
  establishment_end_date_too_late: "establishment_date_end",

  // Mission Manager: key "dates" → actual input
  manager_establishment_date_too_soon: "establishment_date",
  manager_establishment_end_date_before_start: "establishment_date_end",

  // Contract: key "dates" → actual inputs
  start_date_not_before_end_date: ["start_date", "end_date"],
  contract_date_on_conflict_with_another_one: ["start_date", "end_date"],

  // Long Leave: key "dates" → actual inputs
  start_date_before_end_date: ["start_date", "end_date"],

  // Staff/Consultant: key ≠ input
  profile_requires_at_least_one_branch: "assigned_branches",
  not_valid_nationality_id: "nationality",

  // Billing Client: duplicate name/siren
  billing_company_name_taken: "name",
  billing_company_siren_taken: "siren",

  // Subcontractor: key ≠ input
  subcontractor_company_not_found: ["company_name", "siren"],

  // SIREN Luhn validation
  french_siren_should_validate_luhn_algorithm: "siren",

  // Email validation: fallback when API returns key=null (e.g. client)
  // When API provides a specific key (e.g. "personal_email" for staff), it takes priority
  invalid_email: "email",
};

// Override translationKey for specific code+key combinations
// key format: "code:apiKey"
const API_ERROR_OVERRIDES = {
  "not_found:client_id": {
    translationKey: "not_found_client_api_error_msg",
    message: "Client supprimé, merci de rattacher la mission à un client existant.",
  },

  "delivery_note_template_name_duplicate:name": {
    translationKey: "delivery_note_template_name_already_exists",
    message: "Un modèle porte déjà ce nom.",
  },

  "forbidden_length:colleague_ids": {
    translationKey: "delivery_note_share_error_colleagues_length",
    message:
      "Le nombre de destinataires n'est pas accepté, ajustez votre sélection.",
  },
  "forbidden_length:message": {
    translationKey: "delivery_note_share_error_message_length",
    message: "Le message est trop long, raccourcissez-le avant de partager.",
  },
  "colleagues_not_ia:colleague_ids": {
    translationKey: "delivery_note_share_error_colleagues_not_ia",
    message:
      "Un modèle ne peut être partagé qu'avec des collègues IA. Retirez les destinataires concernés.",
  },
  "no_colleague_other_than_self:colleague_ids": {
    translationKey: "delivery_note_share_error_only_self",
    message: "Sélectionnez au moins un collègue autre que vous-même.",
  },
  "internal_error:colleague_ids": {
    translationKey: "delivery_note_share_error_service_unavailable",
    message:
      "Le service est momentanément indisponible. Réessayez dans un instant.",
  },
};

// Codes where the API key should take priority over the static mapping
// because different entities use different field names for the same validation
const API_KEY_PREFERRED_CODES = new Set(["invalid_email"]);

function resolveInputKeys(apiKey, code) {
  const mapped = API_CODE_TO_INPUT[code];
  if (mapped) {
    if (apiKey && API_KEY_PREFERRED_CODES.has(code)) {
      return [apiKey];
    }
    return Array.isArray(mapped) ? mapped : [mapped];
  }
  return apiKey ? [apiKey] : ["__all__"];
}

export function extractApiFieldErrorsFlat(badRequestArray) {
  const result = {};

  if (!badRequestArray) return result;

  if (Array.isArray(badRequestArray)) {
    badRequestArray.forEach((error) => {
      if (!error || typeof error !== "object") return;

      const override = API_ERROR_OVERRIDES[`${error.code}:${error.key}`];

      const errorInfo = {
        code: error.code || null,
        message: override?.message || error.code || "unknown_error",
        params: error.params || {},
        translationKey: override?.translationKey || error.code,
      };

      const inputKeys = resolveInputKeys(error.key, error.code);
      inputKeys.forEach((key) => {
        // Skip superseded codes if a more specific error already exsit
        // such as contract not found and "user not found", simply send back "contract not found"
        if (result[key] && SUPERSEDED_CODES.includes(error.code)) return;
        result[key] = errorInfo;
      });
    });
    return result;
  }

  return result;
}

export const apiResponseHandler = ({
  status,
  missionsEdited,
  successMissions,
  setSuccessMissions,
  changedMissionsIds,
  setChangedMissionsIds,
  apiErrorResponse,
  missionBillsByParameters,
  errorMissions,
  setErrorMissions,
}) => {
  const uniqueMissions = ({ oldMissions, newMissions }) =>
    Array.from(
      new Map(
        [...oldMissions, ...newMissions].map((m) => [m.missionId, m])
      ).values()
    );

  if (status !== QUERY_STATUS.SUCCESS && status !== QUERY_STATUS.ERROR) return;

  if (status === QUERY_STATUS.SUCCESS) {
    const newSuccessMissions =
      missionsEdited?.map((mission) => ({
        missionId: mission?.id,
        missionName: mission?.name,
        isNewUpdate: true,
      })) || [];

    setSuccessMissions(
      uniqueMissions({
        oldMissions: successMissions,
        newMissions: newSuccessMissions,
      })
    );

    const successIds = [
      ...new Set(newSuccessMissions.map((mission) => mission.missionId)),
    ];

    setChangedMissionsIds(
      changedMissionsIds.filter((missionId) => !successIds.includes(missionId))
    );

    setErrorMissions(
      errorMissions?.filter(
        (mission) => !successIds.includes(mission.missionId)
      )
    );
  }

  if (status === QUERY_STATUS.ERROR) {
    const newErrorMissions =
      apiErrorResponse?.map((mission) => ({
        missionId: mission?.missionId,
        missionName: missionBillsByParameters?.find(
          (e) => e?.mission_id === mission?.missionId
        )?.mission_name,
        isNewUpdate: true,
        errors: mission?.errorsDetail,
      })) || [];

    setErrorMissions(
      uniqueMissions({
        oldMissions: errorMissions,
        newMissions: newErrorMissions,
      })
    );
  }
};

export const formattedErrorsMessage = ({ errors }) => {
  const errorsAsArray = Array.isArray(errors) ? errors : [errors];
  const formattedErrors = errorsAsArray?.map((e) => {
    return {
      missionId: e?.missionId,
      errors: Object.values(e?.errors),
    };
  });
  const result = formattedErrors?.map((e) => {
    const errorsDetail = e?.errors?.flat()?.map((e1) => {
      const error = API_ERRORS.find((e2) => e2.code === e1.code)?.errorMessage;
      return {
        ...e1,
        messageId: error?.messageId,
        messageDefault: error?.messageDefault,
      };
    });
    return { missionId: e?.missionId, errorsDetail };
  });
  return result;
};
